import { Home, MapPin, Maximize, Euro, User, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import type { Property } from "@/hooks/useProperties";

interface PropertyCardProps {
  property: Property;
  onClick: () => void;
}

const propertyTypeLabels: Record<string, string> = {
  apartment: "Appartement",
  house: "Maison",
  land: "Terrain",
  commercial: "Commerce",
  other: "Autre",
};

const transactionTypeLabels: Record<string, { label: string; color: string }> = {
  sale: { label: "Vente", color: "bg-blue-500/10 text-blue-600" },
  rental: { label: "Location", color: "bg-purple-500/10 text-purple-600" },
};

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  active: { label: "Actif", variant: "default" },
  pending: { label: "En attente", variant: "secondary" },
  sold: { label: "Vendu", variant: "outline" },
  rented: { label: "Loué", variant: "outline" },
  archived: { label: "Archivé", variant: "secondary" },
};

export function PropertyCard({ property, onClick }: PropertyCardProps) {
  const complianceColor = property.compliance_score >= 80 
    ? "text-green-600" 
    : property.compliance_score >= 50 
      ? "text-amber-600" 
      : "text-red-600";

  const complianceProgressColor = property.compliance_score >= 80
    ? "bg-green-500"
    : property.compliance_score >= 50
      ? "bg-amber-500"
      : "bg-red-500";

  const transactionConfig = transactionTypeLabels[property.transaction_type] || transactionTypeLabels.sale;
  const statusInfo = statusConfig[property.status] || statusConfig.active;

  return (
    <Card 
      className="group cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-primary/30"
      onClick={onClick}
    >
      <CardContent className="p-4">
        {/* Header with badges */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="outline" className={cn("text-xs", transactionConfig.color)}>
              {transactionConfig.label}
            </Badge>
            <Badge variant={statusInfo.variant} className="text-xs">
              {statusInfo.label}
            </Badge>
          </div>
          <div className={cn("text-sm font-semibold", complianceColor)}>
            {property.compliance_score}%
          </div>
        </div>

        {/* Title */}
        <h3 className="font-semibold text-foreground mb-1 line-clamp-1 group-hover:text-primary transition-colors">
          {property.title}
        </h3>

        {/* Address */}
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          <span className="line-clamp-1">{property.address}, {property.city}</span>
        </div>

        {/* Property info */}
        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-3">
          <div className="flex items-center gap-1">
            <Home className="h-3.5 w-3.5" />
            <span>{propertyTypeLabels[property.property_type] || property.property_type}</span>
          </div>
          {property.surface_m2 && (
            <div className="flex items-center gap-1">
              <Maximize className="h-3.5 w-3.5" />
              <span>{property.surface_m2} m²</span>
            </div>
          )}
          {property.price && (
            <div className="flex items-center gap-1">
              <Euro className="h-3.5 w-3.5" />
              <span>{new Intl.NumberFormat("fr-FR").format(property.price)}</span>
            </div>
          )}
        </div>

        {/* Client info */}
        {property.client_name && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
            <User className="h-3.5 w-3.5 shrink-0" />
            <span className="line-clamp-1">{property.client_name}</span>
          </div>
        )}

        {/* Compliance progress */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Conformité dossier</span>
            <span className={cn("font-medium", complianceColor)}>
              {property.compliance_score}%
            </span>
          </div>
          <div className="relative h-1.5 bg-muted rounded-full overflow-hidden">
            <div 
              className={cn("absolute left-0 top-0 h-full rounded-full transition-all", complianceProgressColor)}
              style={{ width: `${property.compliance_score}%` }}
            />
          </div>
        </div>

        {/* Hover action */}
        <div className="flex justify-end mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1">
            Voir le dossier
            <ArrowRight className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
